import { useState, useEffect, type ReactNode } from 'react';
import {
  Shield,
  Lock,
  Settings,
  Users,
  BarChart3,
  Database,
  Key,
  User,
  Zap,
  TrendingUp,
  Bot,
  Wand2,
  ListTree,
  CreditCard,
  Sparkles,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabaseStorage';
import {
  clearAdminSessionStorage,
  parseAdminStaffSession,
  type AdminStaffSession,
} from '../../utils/adminSession';
import { ApiKeysModal } from '../modals/ApiKeysModal';

type NavItem = {
  path: string;
  label: string;
  icon: LucideIcon;
  action?: 'api-keys';
  disabled?: boolean;
  tooltip?: string;
};

const NAV_ITEMS: NavItem[] = [
  { path: '/admin', label: 'Dashboard', icon: BarChart3 },
  { path: '/admin/users', label: 'Users', icon: Users },
  { path: '/admin/models', label: 'All Models', icon: Database },
  { path: '/admin/create', label: 'Create Model', icon: Settings },
  { path: '/admin/pro-forms', label: 'Pro Forms', icon: Settings },
  {
    path: '/admin/promax-models',
    label: 'ProMax Models',
    icon: Zap,
  },
  {
    path: '/admin/agents',
    label: 'AI Agents',
    icon: Bot,
  },
  {
    path: '/admin/agents/autogen-settings',
    label: 'Autogenerate settings',
    icon: Wand2,
  },
  {
    path: '/admin/agents/instances',
    label: 'Agent instances',
    icon: ListTree,
  },
  {
    path: '/admin/billing',
    label: 'Billing',
    icon: CreditCard,
  },
  {
    path: '/admin/creators',
    label: 'Creators',
    icon: Sparkles,
  },
  {
    path: '/admin/creators/queue',
    label: 'Review queue',
    icon: ShieldCheck,
  },
  { path: '/analytics', label: 'Analytics', icon: TrendingUp },
  {
    path: '',
    label: 'API Keys',
    icon: Key,
    action: 'api-keys',
  },
];

function navItemAppliesToPath(pathname: string, item: NavItem): boolean {
  if (item.action === 'api-keys' || !item.path) {
    return false;
  }
  if (item.path === '/analytics') {
    return false;
  }
  const n = pathname.replace(/\/$/, '') || '/';
  const p = item.path.replace(/\/$/, '');
  if (p === '/admin') {
    return n === '/admin' || n === '/admin/';
  }
  if (p === '/admin/pro-forms') {
    return n === p || n.startsWith(p + '/') || n.startsWith('/admin/create-pro-form');
  }
  return n === p || n.startsWith(p + '/');
}

function getBestMatchingNavItem(pathname: string, items: NavItem[], staff: AdminStaffSession | null): NavItem {
  const candidates: NavItem[] = [];
  for (const item of items) {
    if (item.path === '/admin/promax-models' && staff?.role !== 'superadmin') {
      continue;
    }
    if (item.path === '/admin/agents/autogen-settings' && staff?.role !== 'superadmin') {
      continue;
    }
    if (item.path === '/admin/agents/instances' && staff?.role !== 'superadmin') {
      continue;
    }
    if (item.path === '/admin/billing' && staff?.role !== 'superadmin') {
      continue;
    }
    if ((item.path === '/admin/creators' || item.path === '/admin/creators/queue') && staff?.role !== 'superadmin') {
      continue;
    }
    if ((item.path === '/admin/users' || item.path === '/admin/agents') && !staff) {
      continue;
    }
    if (navItemAppliesToPath(pathname, item)) {
      candidates.push(item);
    }
  }
  if (candidates.length === 0) {
    return items[0];
  }
  return candidates.sort((a, b) => b.path.length - a.path.length)[0];
}

function isItemHighlighted(item: NavItem, best: NavItem): boolean {
  if (item.action || !item.path) return false;
  return item.path === best.path;
}

export function AdminPageToolbar({
  title,
  subtitle,
  maxWidthClass = 'max-w-6xl',
  children,
}: {
  title: string;
  subtitle?: string;
  maxWidthClass?: string;
  children?: ReactNode;
}) {
  return (
    <div
      className={`${maxWidthClass} mx-auto w-full px-4 pt-6 pb-4 flex flex-wrap items-center justify-between gap-4 shrink-0`}
    >
      <div className="min-w-0">
        <h1 className="text-lg font-bold text-th-primary tracking-tight">{title}</h1>
        {subtitle ? <p className="text-xs text-th-secondary mt-0.5">{subtitle}</p> : null}
      </div>
      {children ? <div className="flex flex-wrap items-center gap-2">{children}</div> : null}
    </div>
  );
}

interface NavigationDropdownProps {
  currentPath: string;
  onNavigate: (path: string, action: 'navigate' | 'api-keys') => void;
  staffSession: AdminStaffSession | null;
}

const NavigationDropdown = ({ currentPath, onNavigate, staffSession }: NavigationDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const navItems: NavItem[] = NAV_ITEMS.map((item) => {
    if (item.path === '/admin/promax-models') {
      return { ...item, disabled: staffSession?.role !== 'superadmin', tooltip: 'Super Admin only' };
    }
    if (item.path === '/admin/agents/autogen-settings' || item.path === '/admin/agents/instances' || item.path === '/admin/billing') {
      return { ...item, disabled: staffSession?.role !== 'superadmin', tooltip: 'Super Admin only' };
    }
    if (item.path === '/admin/creators' || item.path === '/admin/creators/queue') {
      return { ...item, disabled: staffSession?.role !== 'superadmin', tooltip: 'Super Admin only' };
    }
    if (item.path === '/admin/agents') {
      return { ...item, disabled: !staffSession, tooltip: 'Admin access required' };
    }
    if (item.path === '/analytics') {
      return { ...item, disabled: !staffSession, tooltip: 'Admin access required' };
    }
    if (item.action === 'api-keys') {
      return { ...item, disabled: staffSession?.role !== 'superadmin', tooltip: 'Super Admin only' };
    }
    return item;
  });

  const bestMatch = getBestMatchingNavItem(currentPath, navItems, staffSession);
  const currentItem = bestMatch;

  const handleItemClick = (item: NavItem) => {
    if (item.action === 'api-keys') {
      onNavigate('', 'api-keys');
    } else if (item.path === '/analytics') {
      window.open(item.path, '_blank');
    } else {
      onNavigate(item.path, 'navigate');
    }
    setIsOpen(false);
  };

  const CurrentIcon = currentItem.icon;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-1.5 bg-elevated border border-subtle rounded-lg text-th-primary hover:text-brand hover:border-brand/50 transition-all"
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <CurrentIcon className="w-4 h-4 shrink-0" />
        <span className="text-sm font-medium">{currentItem.label}</span>
        <svg
          className={`w-4 h-4 transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} aria-hidden />
          <div
            className="absolute top-full right-0 mt-1 w-56 max-h-[min(70vh,28rem)] overflow-y-auto bg-elevated border border-subtle rounded-lg shadow-2xl z-20"
            role="menu"
          >
            {navItems.map((item, index) => {
              const ItemIcon = item.icon;
              const active = isItemHighlighted(item, bestMatch);
              return (
                <button
                  key={`${item.label}-${item.path}-${index}`}
                  type="button"
                  role="menuitem"
                  onClick={() => handleItemClick(item)}
                  disabled={item.disabled}
                  className={`w-full flex items-center space-x-3 px-4 py-3 text-left transition-all ${
                    active
                      ? 'bg-brand text-surface'
                      : item.disabled
                        ? 'text-th-secondary/50 cursor-not-allowed opacity-50'
                        : 'text-th-secondary hover:text-th-primary hover:bg-overlay'
                  }`}
                  title={item.tooltip}
                >
                  <ItemIcon className="w-4 h-4 shrink-0" />
                  <span className="text-sm font-medium">{item.label}</span>
                  {item.disabled && <Lock className="w-3 h-3 ml-auto shrink-0" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showApiKeysModal, setShowApiKeysModal] = useState(false);
  const [staffSession, setStaffSession] = useState<AdminStaffSession | null>(null);
  const currentPath = location.pathname;

  useEffect(() => {
    setStaffSession(parseAdminStaffSession());
  }, [currentPath]);

  const goHome = () => {
    navigate('/admin');
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    clearAdminSessionStorage();
    window.location.href = '/admin';
  };

  return (
    <div className="flex h-[100dvh] max-h-[100dvh] min-h-0 flex-col overflow-hidden bg-base">
      <header className="border-b border-subtle bg-surface z-30 shrink-0">
        <div className="max-w-6xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center space-x-3 min-w-0">
              <button
                type="button"
                onClick={goHome}
                className="flex items-center space-x-2 text-th-secondary hover:text-th-primary transition-colors p-2 hover:bg-overlay rounded-lg min-w-0"
                title="Admin home"
              >
                <Shield className="w-5 h-5 text-brand shrink-0" />
                <div className="text-left min-w-0">
                  <h1 className="text-lg font-bold text-th-primary leading-tight truncate">Admin Dashboard</h1>
                  <p className="text-xs text-th-secondary leading-tight">System Administration</p>
                </div>
              </button>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-4 shrink-0">
              <div className="relative">
                <NavigationDropdown
                  currentPath={currentPath}
                  onNavigate={(path, action) => {
                    if (action === 'navigate') {
                      navigate(path);
                    } else if (action === 'api-keys') {
                      if (staffSession?.role === 'superadmin') {
                        setShowApiKeysModal(true);
                      } else {
                        alert('Access denied. Super admin privileges required.');
                      }
                    }
                  }}
                  staffSession={staffSession}
                />
              </div>

              {staffSession && (
                <div className="hidden sm:flex items-center space-x-3 px-3 py-1.5 bg-elevated rounded-lg max-w-[200px]">
                  <User className="w-4 h-4 text-brand shrink-0" />
                  <div className="text-sm min-w-0">
                    <p className="text-th-primary font-medium truncate">{staffSession.name}</p>
                    <p className="text-th-secondary text-xs">
                      {staffSession.role === 'superadmin' ? 'Super Admin' : 'Admin'}
                    </p>
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={handleLogout}
                className="flex items-center space-x-2 px-3 py-2 bg-status-error/20 border border-status-error/30 text-status-error hover:bg-status-error/30 transition-colors rounded-lg"
              >
                <Lock className="w-4 h-4 shrink-0" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 flex flex-col min-h-0 relative">
        <Outlet />
      </div>

      <ApiKeysModal isOpen={showApiKeysModal} onClose={() => setShowApiKeysModal(false)} />

      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute top-3/4 left-1/2 w-80 h-80 bg-status-warning/5 rounded-full blur-3xl" />
      </div>
    </div>
  );
}
